import React from "react";
import { ShieldCheck, Sparkles, AlertTriangle, HelpCircle } from "lucide-react";

/**
 * The four states a moderator can stamp on a before/after post, plus the
 * copy and styling each one uses in the feed and on the moderation page.
 * Keeping this in one place so the badge is impossible to render
 * inconsistently across surfaces.
 */
export const AUTHENTICITY_META = {
  verified_real: {
    label: "Verified real",
    Icon: ShieldCheck,
    hint: "Photos appear natural. Consistent lighting, angle, minimal or no editing.",
    color: "hsl(var(--moss))",
    softBg: "hsl(var(--moss) / 0.14)",
  },
  light_edits: {
    label: "Light edits",
    Icon: Sparkles,
    hint: "Filters or minor retouching present. Result is honest, but softened.",
    color: "hsl(var(--clay))",
    softBg: "hsl(var(--clay) / 0.14)",
  },
  heavy_edits: {
    label: "Heavy edits",
    Icon: AlertTriangle,
    hint: "Significant editing detected. Real results likely differ from what's shown.",
    color: "hsl(var(--terracotta))",
    softBg: "hsl(var(--terracotta) / 0.15)",
  },
  pending: {
    label: "Under review",
    Icon: HelpCircle,
    hint: "A moderator will review these photos for authenticity soon.",
    color: "hsl(var(--muted-foreground))",
    softBg: "hsl(var(--sand) / 0.6)",
  },
};

/** Render nothing when `authenticity` is null/undefined and `showPending`
 *  is false — that's the "not yet queued" state and doesn't need a chip. */
export default function AuthenticityBadge({ authenticity, showPending = true, size = "sm" }) {
  const status = authenticity?.status || (showPending ? "pending" : null);
  if (!status) return null;
  const meta = AUTHENTICITY_META[status];
  if (!meta) return null;
  const { Icon, label } = meta;
  const px = size === "lg" ? "px-3 py-1.5" : "px-2 py-1";
  const iconSize = size === "lg" ? 12 : 11;
  const textCls = size === "lg" ? "text-[11px]" : "text-[10px]";

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${px} ${textCls} rounded-full uppercase tracking-[0.2em] border hairline`}
      style={{ color: meta.color, backgroundColor: meta.softBg, borderColor: "transparent" }}
      title={authenticity?.note || meta.hint}
      data-testid={`authenticity-badge-${status}`}
    >
      <Icon size={iconSize} />
      {label}
    </span>
  );
}
